import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';
import sr from '@utils/sr';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import ubuntuIcon from '../../images/ubuntu.png';
import dockerIcon from '../../images/docker.png';
import lmStudioIcon from '../../images/lmstudio.png';
import uvIcon from '../../images/uv.png';

// --- KEYFRAME ANIMATIONS ---

const gridPulse = keyframes`
  0%, 100% { opacity: 0.15; }
  50% { opacity: 0.3; }
`;

// (Removed unused keyframe animations: signalGlow, chartFlow, ledPulse)

// --- STYLED COMPONENTS ---

const StyledWorkStationPage = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(40px, 6vh, 80px) 0 40px;
  display: flex;
  flex-direction: column;
  gap: 50px;
  font-family: var(--font-sans);

  @media (max-width: 768px) {
    gap: 35px;
    padding: clamp(20px, 4vh, 40px) 0 60px;
  }

  h2.numbered-heading {
    span {
      display: contents;
    }

    @media (max-width: 768px) {
      span {
        display: inline-block;
        flex: 1 1 auto;
      }

      &:after {
        flex: 0 0 60px;
        max-width: 60px;
        width: 60px;
        margin-left: 12px;
      }
    }
  }
`;

// SECTION 1: THE HERO SECTION
const HeroSection = styled.div`
  text-align: center;
  position: relative;
  padding: 30px 20px 20px;
  border-radius: 20px;
  background: radial-gradient(circle at center, rgba(16, 26, 40, 0.3) 0%, transparent 80%);
  border: 1px solid rgba(255, 255, 255, 0.03);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(rgba(255, 255, 255, 0.01) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.01) 1px, transparent 1px);
    background-size: 30px 30px;
    pointer-events: none;
    animation: ${gridPulse} 8s infinite ease-in-out;
  }

  .topology-tag {
    display: inline-block;
    color: #ff2a2a;
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    background: rgba(255, 42, 42, 0.08);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 42, 42, 0.35);
    padding: 5px 15px;
    margin-top: 30px;
    margin-bottom: 15px;

    @media (max-width: 768px) {
      margin-top: 0;
      margin-bottom: 60px;
    }
    border-radius: 50px;
    box-shadow: 0 0 15px rgba(255, 42, 42, 0.25), inset 0 0 10px rgba(255, 42, 42, 0.15);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 42, 42, 0.14);
      border-color: rgba(255, 42, 42, 0.5);
      box-shadow: 0 0 22px rgba(255, 42, 42, 0.4), inset 0 0 12px rgba(255, 42, 42, 0.25);
      color: #fff;
    }
  }

  h1 {
    font-size: clamp(36px, 6vw, 64px);
    font-weight: 800;
    color: var(--white);
    line-height: 1.1;
    margin: 0 0 20px 0;
    letter-spacing: -1px;
    background: linear-gradient(90deg, #ffffff 0%, #ff2a2a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p.subtext {
    max-width: 750px;
    margin: 0 auto;
    color: var(--slate);
    font-size: clamp(16px, 2.5vw, 20px);
    line-height: 1.6;
    font-weight: 400;
  }
`;

// SECTION 2: SYSTEM TOPOLOGY (HARDWARE)
const TopologySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: -30px;

  .section-header {
    display: flex;
    flex-direction: column;
    gap: 8px;

    span.desc {
      color: var(--slate);
      font-size: var(--fz-md);
    }
  }
`;

const HardwareGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 20px;
`;

const HardwareCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 200px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 42, 42, 0.5);
    background: rgba(255, 42, 42, 0.02);
    box-shadow: 0 15px 30px -15px rgba(255, 42, 42, 0.35);

    .icon-gauge {
      color: rgba(255, 42, 42, 1) !important;
      filter: drop-shadow(0 0 8px rgba(255, 42, 42, 0.85)) !important;
    }

    .os-logo-container {
      color: var(--slate) !important;
      filter: none !important;

      .win-icon {
        color: #0078d4 !important;
        filter: drop-shadow(0 0 8px rgba(0, 120, 212, 0.85)) !important;
      }

      .ubuntu-icon-wrapper {
        .ubuntu-img-icon {
          filter: grayscale(0%) brightness(1) contrast(1)
            drop-shadow(0 0 8px rgba(233, 84, 32, 0.85)) !important;
        }
      }
    }

    /* Make the card's main heading adopt the hover color */
    .card-body h3 {
      color: #ff2a2a !important;
    }
  }

  .card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;

    .label {
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      color: var(--green);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .icon-gauge {
      filter: none;
      transition: filter 0.3s ease;
    }

    .os-logo-container {
      display: flex;
      gap: 8px;
      align-items: center;
      color: var(--slate);
      transition: all 0.3s ease;
      width: auto !important;
      height: auto !important;
      min-width: 0 !important;
      min-height: 0 !important;
      max-width: none !important;
      max-height: none !important;

      svg.win-icon {
        width: 22px !important;
        height: 22px !important;
        min-width: 22px !important;
        min-height: 22px !important;
        fill: currentColor !important;
        stroke: none !important;
        transition: all 0.3s ease;
      }

      .ubuntu-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px !important;
        height: 22px !important;
        min-width: 22px !important;
        min-height: 22px !important;

        img.ubuntu-img-icon {
          width: 22px !important;
          height: 22px !important;
          min-width: 22px !important;
          min-height: 22px !important;
          object-fit: contain;
          filter: grayscale(100%) brightness(0.65) contrast(1.2);
          transition: all 0.3s ease;
        }
      }
    }

    svg.icon-gauge {
      width: 28px;
      height: 28px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.5;
      transition: var(--transition);
    }
  }

  .card-body {
    h3 {
      font-size: clamp(20px, 3vw, 24px);
      color: var(--white);
      margin: 0 0 8px 0;
      font-weight: 700;
      line-height: 1.2;
    }

    p.details {
      color: var(--slate);
      font-size: var(--fz-sm);
      line-height: 1.4;
      margin: 0;
    }
  }

  .card-footer {
    margin-top: 20px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--slate);
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    padding: 4px 10px;
    border-radius: 4px;
    width: fit-content;

    @media (max-width: 768px) {
      padding: 4px 10px !important;

      &:hover,
      &:active,
      &:focus {
        transform: none !important;
        cursor: default !important;
        background: rgba(255, 255, 255, 0.04) !important;
        color: var(--slate) !important;
        border: 1px solid rgba(255, 255, 255, 0.06) !important;
      }
    }

    .dot {
      width: 6px !important;
      height: 6px !important;
      min-width: 6px !important;
      min-height: 6px !important;
      flex-shrink: 0 !important;
      padding: 0 !important;
      border-radius: 50% !important;
      background: var(--green) !important;
      border: none !important;
      display: inline-block !important;
      margin: 0 !important;
      box-shadow: none !important;

      &:hover,
      &:active,
      &:focus {
        background: var(--green) !important;
        border: none !important;
        transform: none !important;
        cursor: default !important;
      }
    }
  }
`;

const ToolingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  .section-header {
    display: flex;
    flex-direction: column;
    gap: 8px;

    span.desc {
      color: var(--slate);
      font-size: var(--fz-md);
    }
  }
`;

const ToolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 20px;
`;

const ToolCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 220px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.hoverColor || 'rgba(0, 242, 254, 0.5)'};
    background: ${props => props.hoverBg || 'rgba(0, 242, 254, 0.02)'};
    box-shadow: 0 15px 30px -15px ${props => props.hoverGlow || 'rgba(0, 242, 254, 0.35)'};

    .tool-icon-wrapper {
      color: ${props => props.hoverColor || 'rgba(0, 242, 254, 1)'} !important;
      filter: drop-shadow(
        0 0 8px ${props => props.hoverColor || 'rgba(0, 242, 254, 0.85)'}
      ) !important;
    }

    .tool-icon-wrapper img {
      filter: none !important;
      opacity: 1;
    }

    .tool-icon-wrapper .legion-hud-icon {
      color: #ffffff !important;
      filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.85)) !important;
      .legion-hex {
        fill: url(#legionSpaceGrad) !important;
        stroke: none !important;
      }
    }

    /* Make the card's main heading adopt the hover color (falls back to white) */
    .card-body h3 {
      color: ${props => props.hoverColor || 'var(--white)'} !important;
    }
  }

  .card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 15px;

    .label {
      font-family: var(--font-mono);
      font-size: 11px;
      color: var(--green);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .tool-icon-wrapper {
      color: var(--slate);
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;

      svg {
        width: 24px;
        height: 24px;
        stroke: currentColor;
        fill: none;
        stroke-width: 1.5;
      }
      img {
        width: 24px;
        height: 24px;
        object-fit: contain;
        filter: grayscale(100%) brightness(0.65) contrast(1.2);
        transition: filter 0.2s ease, opacity 0.2s ease;
        opacity: 0.95;
      }
    }
  }

  .card-body {
    margin-bottom: 20px;

    h3 {
      font-size: 18px;
      color: var(--white);
      margin: 0 0 8px 0;
      font-weight: 700;
      line-height: 1.2;
    }

    p.details {
      color: var(--slate);
      font-size: var(--fz-sm);
      line-height: 1.5;
      margin: 0;
    }
  }

  .card-footer {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: auto;

    @media (max-width: 768px) {
      /* Explicitly guarantee ToolCard's footer container itself has no border or background under any state */
      &,
      &:hover,
      &:active,
      &:focus {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        transform: none !important;
        padding: 0 !important;
      }
    }

    span.tag {
      font-family: var(--font-mono);
      font-size: 10px;
      color: var(--light-slate);
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.06);
      padding: 3px 8px;
      border-radius: 4px;
      transition: all 0.3s ease;

      @media (max-width: 768px) {
        &:hover,
        &:active,
        &:focus {
          transform: none !important;
          cursor: default !important;
          background: rgba(255, 255, 255, 0.04) !important;
          color: var(--light-slate) !important;
          border: 1px solid rgba(255, 255, 255, 0.06) !important;
        }
      }
    }
  }
`;

// --- WEB AUDIO API SWITCH CLICK ENGINE ---

const playMechanicalClick = (type = 'standard') => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      return;
    }
    const ctx = new AudioContext();
    const now = ctx.currentTime;

    // Plunger Transient (High frequency tactile contact click)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(type === 'enter' ? 950 : 1150, now);
    osc1.frequency.exponentialRampToValueAtTime(150, now + 0.015);
    gain1.gain.setValueAtTime(type === 'enter' ? 0.1 : 0.07, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.018);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);

    // Housing Bottom-out Transient (Resonant switch body)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(type === 'enter' ? 200 : 230, now);
    osc2.frequency.exponentialRampToValueAtTime(80, now + 0.03);
    gain2.gain.setValueAtTime(type === 'enter' ? 0.07 : 0.04, now + 0.002);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.035);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);

    // Spring Rattle (Filtered white noise decay)
    const bufferSize = ctx.sampleRate * 0.02;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = buffer;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.setValueAtTime(8000, now);
    noiseFilter.Q.setValueAtTime(3, now);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.01, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);
    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(ctx.destination);

    // Play sounds
    osc1.start(now);
    osc1.stop(now + 0.02);
    osc2.start(now);
    osc2.stop(now + 0.04);
    noiseSource.start(now);
    noiseSource.stop(now + 0.025);
  } catch (e) {
    // Fail silently on context blocks
  }
};

// --- COMPONENT IMPLEMENTATION ---

const Workstation = ({ showWorkstation }) => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || !showWorkstation) {
      return;
    }

    const node = revealContainer.current;
    if (!node || !sr || typeof sr.reveal !== 'function') {
      return;
    }

    try {
      // Use a very low viewFactor (0.01) so ScrollReveal triggers immediately at scroll position 0
      sr.reveal(node, srConfig(150, 0.01));
    } catch (e) {
      // ignore
    }

    // Force ScrollReveal check by dispatching scroll event after layout settles
    const timer = setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('scroll'));
      }
    }, 150);

    return () => {
      clearTimeout(timer);
      try {
        if (typeof sr.clean === 'function') {
          sr.clean(node);
        }
      } catch (e) {
        // ignore
      }
    };
  }, [prefersReducedMotion, showWorkstation]);

  // (removed unused telemetry state)

  const hardwareSpecs = [
    {
      label: 'Processor',
      val: 'Intel® Core™ Ultra 9 275HX',
      desc: '24 Cores / 24 Threads | up to 5.4 GHz (36 MB Cache)',
      tag: 'Arrow Lake HX',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="icon-gauge"
          style={{
            width: '28px',
            height: '28px',
            color: 'var(--slate)',
            transition: 'all 0.3s ease',
          }}
        >
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <rect x="8" y="8" width="8" height="8" rx="1" strokeDasharray="2 1" />
          <path d="M12 1v2M8 1v2M16 1v2M12 21v2M8 21v2M16 21v2M1 12h2M1 8h2M1 16h2M21 12h2M21 8h2M21 16h2" />
        </svg>
      ),
    },
    {
      label: 'Graphics',
      val: 'NVIDIA® GeForce RTX™ 5060',
      desc: '8GB GDDR7 | Optimized for Local LLM Inferences & PyTorch Training',
      tag: 'Local ML Inference',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="icon-gauge"
          style={{
            width: '28px',
            height: '28px',
            color: 'var(--slate)',
            transition: 'all 0.3s ease',
          }}
        >
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="7" cy="12" r="3.5" />
          <circle cx="17" cy="12" r="3.5" />
          <circle cx="7" cy="12" r="1" fill="currentColor" />
          <circle cx="17" cy="12" r="1" fill="currentColor" />
          <path d="M5 18v2M9 18v2M13 18v2M17 18v2" />
        </svg>
      ),
    },
    {
      label: 'Memory',
      val: '32GB DDR5 RAM',
      desc: '5600 MHz Dual-Channel | High-bandwidth configuration for data tasks',
      tag: 'Dual-Channel 5600MHz',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="icon-gauge"
          style={{
            width: '28px',
            height: '28px',
            color: 'var(--slate)',
            transition: 'all 0.3s ease',
          }}
        >
          <rect x="3" y="7" width="18" height="10" rx="1.5" />
          <path d="M6 7v3M10 7v3M14 7v3M18 7v3M4 17v1.5M8 17v1.5M12 17v1.5M16 17v1.5M20 17v1.5" />
          <rect
            x="6"
            y="11"
            width="12"
            height="3"
            rx="0.5"
            fill="currentColor"
            style={{ opacity: 0.1 }}
          />
        </svg>
      ),
    },
    {
      label: 'Storage',
      val: '1 TB PCIe® Gen4 SSD',
      desc: 'M.2 NVMe | Ultra-fast data loading (7000+ MB/s Sequential Reads)',
      tag: 'NVMe Gen4 x4',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="icon-gauge"
          style={{
            width: '28px',
            height: '28px',
            color: 'var(--slate)',
            transition: 'all 0.3s ease',
          }}
        >
          <rect x="2" y="6" width="20" height="12" rx="1" />
          <line x1="7" y1="6" x2="7" y2="18" strokeDasharray="1 1" />
          <rect x="10" y="8" width="4" height="8" rx="0.5" />
          <rect x="16" y="8" width="4" height="8" rx="0.5" />
          <circle cx="4.5" cy="12" r="1" fill="currentColor" />
        </svg>
      ),
    },
    {
      label: 'Display',
      val: '16" 2.5K G-SYNC Display',
      desc: '2560x1600 WQXGA IPS | 240Hz | Peak 100% sRGB Split-Screen Space',
      tag: '240Hz WQXGA',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="icon-gauge"
          style={{
            width: '28px',
            height: '28px',
            color: 'var(--slate)',
            transition: 'all 0.3s ease',
          }}
        >
          <rect x="2" y="3" width="20" height="13" rx="2" />
          <path d="M12 16v5M8 21h8" />
          <path d="M5 6h14v7H5z" fill="currentColor" style={{ opacity: 0.05 }} />
        </svg>
      ),
    },
    {
      label: 'Operating System',
      val: 'Windows 11 + WSL2',
      desc: 'Dual-kernel environment running Windows 11 Home & Ubuntu 22.04 LTS for secure, native Linux CLI development.',
      tag: 'Windows & Ubuntu Linux',
      logo: (
        <div
          className="os-logo-container"
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            transition: 'all 0.3s ease',
            color: 'var(--slate)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            className="win-icon"
            fill="currentColor"
            style={{ width: '22px', height: '22px', transition: 'all 0.3s ease' }}
          >
            <path d="M0 0h11.3v11.3H0zm12.7 0H24v11.3H12.7zM0 12.7h11.3V24H0zm12.7 0H24V24H12.7z" />
          </svg>
          <div
            className="ubuntu-icon-wrapper"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '22px',
              height: '22px',
            }}
          >
            <img src={ubuntuIcon} alt="Ubuntu" className="ubuntu-img-icon" />
          </div>
        </div>
      ),
    },
  ];

  const customTools = [
    {
      label: 'IDEs & Editors',
      val: 'IDE & Agentic Workspace',
      desc: 'Visual Studio Code, Cursor, Microsoft Visual Studio Community 2026, and the advanced Antigravity 2.0 Agentic IDE workspace configured for bleeding-edge software development.',
      tags: ['Antigravity 2.0', 'VS Code', 'Cursor', 'Visual Studio 2026'],
      hoverColor: '#ff2a2a',
      hoverGlow: 'rgba(255, 42, 42, 0.35)',
      hoverBg: 'rgba(255, 42, 42, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="2" y="3" width="20" height="18" rx="2" />
          <path d="M2 8h20" />
          <path d="M8 3v5" />
          <path d="M12 13l-2 2 2 2" />
          <path d="M15 13l2 2-2 2" />
        </svg>
      ),
    },
    {
      label: 'Terminal & Shell',
      val: 'WSL2 Terminal & Zsh',
      desc: 'Windows Terminal hosting WSL2 Ubuntu CLI. Customized with Zsh shell, Powerlevel10k theme, auto-suggestions, and directory bookmarks, rendered in JetBrains Mono Nerd Font.',
      tags: ['Windows Terminal', 'WSL2 Ubuntu', 'Zsh', 'P10k'],
      hoverColor: '#4af626',
      hoverGlow: 'rgba(74, 246, 38, 0.35)',
      hoverBg: 'rgba(74, 246, 38, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <polyline points="4 17 10 11 4 5" />
          <line x1="12" y1="19" x2="20" y2="19" />
        </svg>
      ),
    },
    {
      label: 'Local Orchestration',
      val: 'Docker Desktop Engine',
      desc: 'Local Docker Desktop containerization suite powered by WSL2 backends, utilized to orchestrate, debug, and run microservice databases and development pipelines.',
      tags: ['Docker Desktop', 'WSL2 Backend', 'Orchestration'],
      hoverColor: '#0db7ed',
      hoverGlow: 'rgba(13, 183, 237, 0.35)',
      hoverBg: 'rgba(13, 183, 237, 0.02)',
      logo: (
        <img
          src={dockerIcon}
          alt="Docker"
          style={{ width: 22, height: 22, objectFit: 'contain' }}
        />
      ),
    },
    {
      label: 'Version Control Systems',
      val: 'Git & Distributed VCS',
      desc: 'Git CLI and Git Bash integration with GitHub Desktop, allowing seamless branch tracking and local repo history audits.',
      tags: ['Git Bash', 'GitHub Desktop', 'VCS'],
      hoverColor: '#f05032',
      hoverGlow: 'rgba(240, 80, 50, 0.35)',
      hoverBg: 'rgba(240, 80, 50, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <circle cx="18" cy="18" r="3" />
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M6 9v6" />
          <path d="M9 6h3a4 4 0 0 1 4 4v5" />
        </svg>
      ),
    },
    {
      label: 'Developer Runtimes',
      val: 'Languages, SDKs & Compilers',
      desc: 'Bleeding-edge system runtimes and developer compiler toolchains including Java SE JDK 25, Python 3.14.3, .NET Framework, and Node.js.',
      tags: ['Java 25', 'Python 3.14', 'Node.js', '.NET SDK'],
      hoverColor: '#e76f00',
      hoverGlow: 'rgba(231, 111, 0, 0.35)',
      hoverBg: 'rgba(231, 111, 0, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <path
            d="M8 3H7a3 3 0 0 0-3 3v3a3 3 0 0 1-3 3 3 3 0 0 1 3 3v3a3 3 0 0 0 3 3h1"
            strokeLinecap="round"
          />
          <path
            d="M16 3h1a3 3 0 0 1 3 3v3a3 3 0 0 0 3 3 3 3 0 0 0-3 3v3a3 3 0 0 1-3 3h-1"
            strokeLinecap="round"
          />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
          <path d="M12 8v2M12 14v2M8 12h2M14 12h2" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      label: 'Package Management',
      val: 'Astral uv Python Runtime',
      desc: 'Extremely fast Python package resolver and installer written in Rust, delivering instantaneous virtual environments and dependency locking.',
      tags: ['uv Manager', 'Rust-powered', 'Python env'],
      hoverColor: '#38bdf8',
      hoverGlow: 'rgba(56, 189, 248, 0.35)',
      hoverBg: 'rgba(56, 189, 248, 0.02)',
      logo: (
        <img src={uvIcon} alt="Astral uv" style={{ width: 22, height: 22, objectFit: 'contain' }} />
      ),
    },
    {
      label: 'Local Databases',
      val: 'MongoDB 8.2.3 Server',
      desc: 'Local high-performance document database engine paired with MongoDB Compass GUI for structured query audits and database management.',
      tags: ['MongoDB 8.2.3', 'NoSQL', 'Compass GUI'],
      hoverColor: '#4db33d',
      hoverGlow: 'rgba(77, 179, 61, 0.35)',
      hoverBg: 'rgba(77, 179, 61, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <path d="M12 2c0 0-6 4-6 10s4 8 6 10c2-2 6-4 6-10S12 2 12 2z" />
          <path d="M12 2v20" />
          <path d="M12 6c1.5 1.5 3 3.5 3 6" />
        </svg>
      ),
    },
    {
      label: 'Local Artificial Intelligence',
      val: 'LM Studio & Ollama',
      desc: 'Offline LLM playgrounds for hosting local open weights (Llama/Mistral) and running background Ollama services for local AI coding companion models.',
      tags: ['LM Studio', 'Ollama daemon', 'Local Inference'],
      hoverColor: '#a855f7',
      hoverGlow: 'rgba(168, 85, 247, 0.35)',
      hoverBg: 'rgba(168, 85, 247, 0.02)',
      logo: (
        <img
          src={lmStudioIcon}
          alt="LM Studio & Ollama"
          style={{ width: 22, height: 22, objectFit: 'contain' }}
        />
      ),
    },
    {
      label: 'AI Application Engine',
      val: 'Pinokio AI Launcher',
      desc: 'Advanced autonomous browser and launcher designed to script, install, and execute local machine learning models, neural WebUIs, and LLM workspaces with one click.',
      tags: ['Pinokio', 'One-Click AI', 'WebUIs'],
      hoverColor: '#ec4899',
      hoverGlow: 'rgba(236, 72, 153, 0.35)',
      hoverBg: 'rgba(236, 72, 153, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <circle cx="11" cy="11" r="7.5" />
          <circle cx="8" cy="10" r="1" fill="currentColor" />
          <circle cx="14" cy="10" r="1" fill="currentColor" />
          <path d="M9.5 14c1 0.8 2 0.8 3 0" strokeLinecap="round" />
          <path d="M11 11h9.5" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      label: 'AI Conversational Research',
      val: 'Perplexity AI (Comet Client)',
      desc: 'Dedicated Perplexity desktop workspace powered by the Comet application core, allowing native OS conversational AI search, code research, and technical analysis.',
      tags: ['Perplexity', 'Comet App', 'AI Search', 'Research'],
      hoverColor: '#22c55e',
      hoverGlow: 'rgba(34, 197, 94, 0.35)',
      hoverBg: 'rgba(34, 197, 94, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <path
            d="M11 7l1 2 2 1-2 1-1 2-1-2-2-1 2-1z"
            fill="currentColor"
            style={{ opacity: 0.2 }}
          />
        </svg>
      ),
    },
    {
      label: 'Sandbox Virtualization',
      val: 'VMware Workstation Pro',
      desc: 'Local hypervisor virtualization engine to host enterprise-grade sandbox development machines and securely test complex virtual network architectures.',
      tags: ['Workstation Pro 17', 'Hypervisor', 'Virtual Networks'],
      hoverColor: '#ee7023',
      hoverGlow: 'rgba(238, 112, 35, 0.35)',
      hoverBg: 'rgba(238, 112, 35, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="3" y="3" width="8" height="8" rx="1.5" />
          <rect x="13" y="3" width="8" height="8" rx="1.5" />
          <rect x="3" y="13" width="8" height="8" rx="1.5" />
          <path d="M11 7h2M7 11v2M11 11l2 2" strokeDasharray="1 1" />
          <rect
            x="13"
            y="13"
            width="8"
            height="8"
            rx="1.5"
            strokeDasharray="2 2"
            style={{ opacity: 0.5 }}
          />
        </svg>
      ),
    },
    {
      label: 'Compiler Toolchain',
      val: 'MSYS2 Compiler Suite',
      desc: 'Unix shell sandbox providing the pacman package manager, gcc/clang/g++ compilers, and MSYS/MinGW environments for standard C++ compilation.',
      tags: ['MSYS2', 'MinGW-w64', 'Clang compiler', 'GCC'],
      hoverColor: '#eab308',
      hoverGlow: 'rgba(234, 179, 8, 0.35)',
      hoverBg: 'rgba(234, 179, 8, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <polyline points="7 8 11 12 7 16" />
          <line x1="13" y1="16" x2="17" y2="16" />
        </svg>
      ),
    },
    {
      label: 'API & Secure Networking',
      val: 'ngrok & Postman API Tools',
      desc: 'Secure tunneling with ngrok to expose local web servers to webhooks, paired with Postman client for automated REST API diagnostic calls and tests.',
      tags: ['ngrok tunnel', 'Postman', 'Webhooks', 'REST API'],
      hoverColor: '#f97316',
      hoverGlow: 'rgba(249, 115, 22, 0.35)',
      hoverBg: 'rgba(249, 115, 22, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          <path d="M2 12h20" />
        </svg>
      ),
    },
    {
      label: 'Secure WAN Routing',
      val: 'Proton VPN Tunneling',
      desc: 'High-security Swiss encrypted virtual private network to protect remote operations and securely audit geo-localized application distributions.',
      tags: ['Proton VPN', 'IP Cryptography', 'Secure WAN'],
      hoverColor: '#10b981',
      hoverGlow: 'rgba(16, 185, 129, 0.35)',
      hoverBg: 'rgba(16, 185, 129, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
    },
    {
      label: 'Hardware Hub',
      val: 'Legion Space Dashboard',
      desc: 'Lenovo\'s official unified gaming interface to orchestrate gaming runtime profiles, controller mapping directories, and overlay diagnostics.',
      tags: ['Legion Space', 'System Overlay', 'Hardware Control'],
      hoverColor: '#e62229',
      hoverGlow: 'rgba(230, 34, 41, 0.35)',
      hoverBg: 'rgba(230, 34, 41, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon legion-hud-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <defs>
            <linearGradient id="legionSpaceGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4f46e5" />
              <stop offset="100%" stopColor="#06b6d4" />
            </linearGradient>
          </defs>
          <polygon
            points="12 2 21 7 21 17 12 22 3 17 3 7"
            className="legion-hex"
            style={{
              transition: 'all 0.3s ease',
              fill: 'none',
              stroke: 'currentColor',
            }}
          />
          <path d="M 8.5 9.5 A 5 5 0 0 1 15.5 9.5" strokeWidth="2.5" strokeLinecap="butt" />
          <path d="M 12 16.5 A 5 5 0 0 1 8 10.5" strokeWidth="2.5" strokeLinecap="butt" />
          <path d="M 16 10.5 A 5 5 0 0 1 12 16.5" strokeWidth="2.5" strokeLinecap="butt" />
        </svg>
      ),
    },
    {
      label: 'Hardware Tuning',
      val: 'Diagnostics & Telemetry',
      desc: 'Real-time telemetry and benchmarking suite (HWiNFO64, TechPowerUp GPU-Z, and FurMark2) utilized to audit system workloads and thermals.',
      tags: ['HWiNFO64', 'GPU-Z', 'FurMark2', 'Telemetry'],
      hoverColor: '#ff5f56',
      hoverGlow: 'rgba(255, 95, 86, 0.35)',
      hoverBg: 'rgba(255, 95, 86, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      ),
    },
    {
      label: 'Display Optimization',
      val: 'X-Rite Color Assistant',
      desc: 'Color profiles and laptop panel calibration tooling to enforce strict 100% sRGB accuracy for professional digital mockup designs and high-fidelity layouts.',
      tags: ['X-Rite', 'Color Profile', '100% sRGB'],
      hoverColor: '#3b82f6',
      hoverGlow: 'rgba(59, 130, 246, 0.35)',
      hoverBg: 'rgba(59, 130, 246, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M21 12H3" />
          <path d="M12 3v18" />
          <circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" />
          <circle cx="16.5" cy="7.5" r="1.5" fill="currentColor" />
          <circle cx="7.5" cy="16.5" r="1.5" fill="currentColor" />
          <circle cx="16.5" cy="16.5" r="1.5" fill="currentColor" />
        </svg>
      ),
    },
    {
      label: 'Workflow Utilities',
      val: 'Microsoft PowerToys',
      desc: 'Advanced workflow customizations including FancyZones for window splits, Keyboard Manager for globally mapped macros, and active Awake state locks.',
      tags: ['FancyZones', 'Keyboard Manager', 'Awake'],
      hoverColor: '#0078d4',
      hoverGlow: 'rgba(0, 120, 212, 0.35)',
      hoverBg: 'rgba(0, 120, 212, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <path d="M12 8v8M8 12h8" strokeWidth="1" strokeDasharray="1 1" />
        </svg>
      ),
    },
    {
      label: 'System Optimization',
      val: 'winutil Optimizer',
      desc: 'Chris Titus Tech\'s advanced Windows debloater, optimizer, and system registry package manager, keeping background workloads at absolute peak performance.',
      tags: ['winutil', 'OS Debloater', 'Micro-Tweaks'],
      hoverColor: '#a855f7',
      hoverGlow: 'rgba(168, 85, 247, 0.35)',
      hoverBg: 'rgba(168, 85, 247, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z" />
        </svg>
      ),
    },
    {
      label: 'Desktop Aesthetics',
      val: 'Desktop Customizations',
      desc: 'Transparent UI styling fueled by TranslucentTB borderless taskbars, live system monitor diagnostics, and custom Wallpaper Engine parallax canvas spaces.',
      tags: ['TranslucentTB', 'Wallpaper Engine', 'System Diagnostic'],
      hoverColor: '#ff2a5f',
      hoverGlow: 'rgba(255, 42, 95, 0.35)',
      hoverBg: 'rgba(255, 42, 95, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
          <path d="M12 6V12L16 14" />
        </svg>
      ),
    },
    {
      label: 'Remote Management',
      val: 'RustDesk & AnyDesk',
      desc: 'High-performance remote control and cross-platform desktop management clients utilized to securely monitor and administer virtual network hosts.',
      tags: ['RustDesk', 'AnyDesk', 'Remote Control'],
      hoverColor: '#ef4444',
      hoverGlow: 'rgba(239, 68, 68, 0.35)',
      hoverBg: 'rgba(239, 68, 68, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="2" y="4" width="12" height="10" rx="2" />
          <rect x="10" y="10" width="12" height="10" rx="2" />
          <path d="M6 14v2h4" strokeDasharray="2 2" />
        </svg>
      ),
    },
    {
      label: 'System Blueprinting',
      val: 'StarUML Architecture',
      desc: 'High-fidelity modeling tool to construct UML, Entity Relationship (ERD), and complex software layout design matrices before coding.',
      tags: ['StarUML', 'UML Modeler', 'ERD Diagrams'],
      hoverColor: '#0284c7',
      hoverGlow: 'rgba(2, 132, 199, 0.35)',
      hoverBg: 'rgba(2, 132, 199, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="3" y="3" width="6" height="5" rx="1" />
          <rect x="15" y="3" width="6" height="5" rx="1" />
          <rect x="9" y="16" width="6" height="5" rx="1" />
          <line x1="6" y1="8" x2="6" y2="12" />
          <line x1="18" y1="8" x2="18" y2="12" />
          <line x1="6" y1="12" x2="18" y2="12" />
          <line x1="12" y1="12" x2="12" y2="16" />
        </svg>
      ),
    },
    {
      label: 'OS Image Flash Utility',
      val: 'balenaEtcher',
      desc: 'High-speed image flashing application to burn OS boot configurations and ISO files into flash keys and memory card modules securely.',
      tags: ['balenaEtcher', 'Image Flashing', 'ISO/Bootable'],
      hoverColor: '#10b981',
      hoverGlow: 'rgba(16, 185, 129, 0.35)',
      hoverBg: 'rgba(16, 185, 129, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <rect x="6" y="2" width="12" height="20" rx="2" />
          <path d="M10 6h4" />
          <path d="M10 10h4" />
          <circle cx="12" cy="16" r="2" />
        </svg>
      ),
    },
    {
      label: 'CLI Multimedia',
      val: 'FFmpeg Tooling',
      desc: 'Terminal-based video transcoding, stream multiplexing, and cross-platform digital media filtering tool.',
      tags: ['FFmpeg', 'CLI Video/Audio', 'Transcoding'],
      hoverColor: '#00e5a3',
      hoverGlow: 'rgba(0, 229, 163, 0.35)',
      hoverBg: 'rgba(0, 229, 163, 0.02)',
      logo: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="tool-icon"
          style={{ width: '22px', height: '22px' }}
        >
          <circle cx="12" cy="12" r="10" />
          <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" style={{ opacity: 0.2 }} />
        </svg>
      ),
    },
  ];

  return (
    <StyledWorkStationPage id="workstation" ref={revealContainer}>
      {/* 🏗️ SECTION 1: THE HERO SECTION */}
      <HeroSection>
        <span className="topology-tag">Workstation & System Architecture</span>
        <h1>The Production Environment</h1>
        <p className="subtext">
          A fine-tuned environment engineered for deep learning inference, local container
          orchestration, and seamless full-stack development workflows.
        </p>
      </HeroSection>

      {/* 🏗️ SECTION 2: WORKSPACE & SYSTEM TOPOLOGY (HARDWARE) */}
      <TopologySection>
        <div className="section-header">
          <h2 className="numbered-heading">
            <span>Compute & Host Infrastructure</span>
          </h2>
          <span className="desc">
            Local compute resources configured for heavy development and inference pipelines.
          </span>
        </div>
        <HardwareGrid>
          {hardwareSpecs.map((spec, i) => (
            <HardwareCard
              key={i}
              onClick={() => {
                playMechanicalClick('standard');
              }}
            >
              <div className="card-top">
                <span className="label">{spec.label}</span>
                {spec.logo}
              </div>
              <div className="card-body">
                <h3>{spec.val}</h3>
                <p className="details">{spec.desc}</p>
              </div>
              <div className="card-footer">
                <span
                  className="dot"
                  style={{
                    width: '6px',
                    height: '6px',
                    background: 'var(--green)',
                    borderRadius: '50%',
                  }}
                />
                {spec.tag}
              </div>
            </HardwareCard>
          ))}
        </HardwareGrid>
      </TopologySection>

      {/* 🏗️ SECTION 3: DEVELOPMENT TOOLING & CUSTOMIZATIONS */}
      <ToolingSection>
        <div className="section-header">
          <h2 className="numbered-heading">
            <span>Software, Tooling & Customizations</span>
          </h2>
          <span className="desc">
            Custom development environments, deep systems virtualization, and power-user
            optimizations.
          </span>
        </div>
        <ToolGrid>
          {customTools.map((tool, i) => (
            <ToolCard
              key={i}
              hoverColor={tool.hoverColor}
              hoverGlow={tool.hoverGlow}
              hoverBg={tool.hoverBg}
              onClick={() => {
                playMechanicalClick('standard');
              }}
            >
              <div className="card-top">
                <span className="label">{tool.label}</span>
                <div className="tool-icon-wrapper">{tool.logo}</div>
              </div>
              <div className="card-body">
                <h3>{tool.val}</h3>
                <p className="details">{tool.desc}</p>
              </div>
              <div className="card-footer">
                {tool.tags.map((tag, j) => (
                  <span key={j} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            </ToolCard>
          ))}
        </ToolGrid>
      </ToolingSection>
    </StyledWorkStationPage>
  );
};

Workstation.propTypes = {
  showWorkstation: PropTypes.bool,
};

export default Workstation;
